import { useState, useEffect, useMemo, useCallback } from 'react';
import { FileQuestion, CheckCircle2, Clock, HelpCircle } from 'lucide-react';
import { Card, HStack } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon, Badge, Tooltip } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Switch } from '@components/base/inputs';
import { Alert, Spinner } from '@components/base/feedback';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
} from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { taskService } from '../api/taskService';
import type { VeosPieceToRequest } from '../api/taskService';
import { WorkflowStepTracker } from './WorkflowStepTracker';
import type { WorkflowStepExecutionSummary } from './WorkflowStepTracker';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RequestPiecesCardProps {
  idSin: string | number;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ both the DataList column config and the EntityForm field
   * config are returned unchanged (byte-identical render).
   *
   * Rule authors target the **stable keys** below to rename / hide / reorder /
   * tooltip a surface without code:
   *  - DataList columns: `demande` (the interactive Switch toggle column),
   *    `libelle` (piece label), `statut` (En attente / Reçu badge).
   *  - EntityForm field: `submit` (the Enregistrer button label/visibility).
   *
   * The card always owns the Switch state, the request/cancel split, and the
   * single `submitPieceRequests` workflow call — overrides only mutate the
   * declarative presentation, never the workflow path.
   */
  _layoutOverrides?: LayoutOverrides;
}

const TOOLTIP_TEXT =
  'Activez le switch pour demander une pièce, désactivez-le pour annuler une demande en attente. Les modifications sont signalées par un point bleu.';

// ---------------------------------------------------------------------------
// Per-row render context
//
// Custom cell renderers are module-singletons (registered once below), but the
// `demande` Switch must stay controlled by the card's live `effectiveState`
// and the modified-row dot must reflect the just-toggled row. Both pieces of
// per-instance, per-render state are carried on each row object under `__ctx`,
// which the renderers read from `row` at render time. The rows are rebuilt
// every render (see `rows` useMemo) so the closures stay fresh — no
// module-global mutable state, multi-instance safe. Mirrors MissingPiecesCard.
// ---------------------------------------------------------------------------

interface PieceRowContext {
  /** Effective Switch state for this row (request ON / cancel OFF). */
  checked: boolean;
  /** Whether this row differs from its initial server state. */
  isModified: boolean;
  /** Submitted → the Switch is locked (read-only). */
  locked: boolean;
  /** Toggle this row's Switch (client-side only — no API call). */
  onToggle: () => void;
}

interface PieceRow extends VeosPieceToRequest {
  /** Derived status for the `statut` badge column: 'En attente' | 'Reçu' | ''. */
  STATUT_DERIVED: string;
  __ctx: PieceRowContext;
}

function getRowContext(row: Record<string, unknown>): PieceRowContext | undefined {
  return (row as Partial<PieceRow>).__ctx ?? undefined;
}

// ---------------------------------------------------------------------------
// Custom cell renderers
//
// Registered once at module load (mirroring MissingPiecesCard /
// CostSynthesisCard). They read the row's `__ctx` for live state + toggle
// callback so the singleton renderer always calls the *current* card state.
// ---------------------------------------------------------------------------

const TOGGLE_CELL_RENDERER = 'claimflow-requestPieceToggle';
const STATUT_CELL_RENDERER = 'claimflow-requestPieceStatut';

dataListWorkflowRegistry.registerCellRenderer(TOGGLE_CELL_RENDERER, (_value, row) => {
  const piece = row as unknown as VeosPieceToRequest;
  const ctx = getRowContext(row);
  const checked = ctx?.checked ?? false;
  const isModified = ctx?.isModified ?? false;
  return (
    <span className="inline-flex items-center gap-2">
      <Switch
        checked={checked}
        onChange={() => ctx?.onToggle()}
        disabled={ctx?.locked ?? false}
        aria-label={piece.LIB_PIE}
      />
      {isModified && (
        <span
          data-testid={`row-modified-dot-${piece.CD_PIE}`}
          className="inline-block h-2 w-2 rounded-full bg-primary-600"
          aria-hidden="true"
        />
      )}
    </span>
  );
});

dataListWorkflowRegistry.registerCellRenderer(STATUT_CELL_RENDERER, (_value, row) => {
  const status = (row as Partial<PieceRow>).STATUT_DERIVED ?? '';
  if (status === 'En attente') {
    return (
      <Badge variant="warning" pill>
        <span className="inline-flex items-center gap-1 normal-case tracking-normal">
          <Clock className="w-3 h-3" />
          <span>En attente</span>
        </span>
      </Badge>
    );
  }
  if (status === 'Reçu') {
    return (
      <Badge variant="success" pill>
        <span className="normal-case tracking-normal">Reçu</span>
      </Badge>
    );
  }
  return null;
});

// ---------------------------------------------------------------------------
// Row model
//
// Pure builder for the enriched DataList rows. Reproduces the per-row
// derivations the original `PieceRow` computed inline:
//   • CHECKED        = effective Switch state
//   • IS_MODIFIED    = CHECKED differs from the initial (EN_ATTENTE === 1)
//   • LOCKED         = submitted (Switch read-only)
//   • STATUT_DERIVED = 'En attente' when ON; 'Reçu' when RECUE && OFF; else ''
// Passthrough VeosPieceToRequest fields stay on the row for the split logic.
// ---------------------------------------------------------------------------

function buildPieceRows(
  pieces: VeosPieceToRequest[],
  effectiveState: (cdPie: string) => boolean,
  submitted: boolean,
  onToggle: (cdPie: string, nextChecked: boolean) => void,
): PieceRow[] {
  return pieces.map((piece) => {
    const checked = effectiveState(piece.CD_PIE);
    const isModified = checked !== (piece.EN_ATTENTE === 1);
    let statutDerived = '';
    if (checked) {
      statutDerived = 'En attente';
    } else if (piece.RECUE === 1) {
      statutDerived = 'Reçu';
    }
    return {
      ...piece,
      STATUT_DERIVED: statutDerived,
      __ctx: {
        checked,
        isModified,
        locked: submitted,
        onToggle: () => onToggle(piece.CD_PIE, !checked),
      },
    };
  });
}

// ---------------------------------------------------------------------------
// DataList config (recipe B)
//
// Declarative columns with the three stable keys. French labels live only in
// `column.label` (display-only / overridable). The interactive Switch is a
// first-class `custom` column (`demande`) so rules can rename/hide/reorder it.
// The modified-row highlight is carried by `formatting.rowRules` on the
// `IS_MODIFIED` flag (re-expressing the original wrapper's Tailwind treatment),
// while the blue dot lives inside the `demande` cell. No toolbar / pagination /
// sort / totals — the design is a plain, fixed list.
// ---------------------------------------------------------------------------

function createRequestPiecesConfig(rows: PieceRow[]): DataListConfig {
  return {
    id: 'claimflow.requestPiecesCard.list',

    dataSource: {
      data: rows as unknown as Record<string, unknown>[],
      fieldMapping: { libelle: 'LIB_PIE' },
    },

    columns: {
      demande: {
        label: 'Demande',
        order: 1,
        align: 'left',
        type: 'custom',
        typeOptions: { renderer: TOGGLE_CELL_RENDERER },
        sortable: false,
      },
      libelle: {
        label: 'Pièce',
        order: 2,
        align: 'left',
        type: 'text',
        sortable: false,
      },
      statut: {
        label: 'Statut',
        order: 3,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: STATUT_CELL_RENDERER },
        sortable: false,
      },
    },

    display: {
      size: 'compact',
      hoverable: false,
    },

    sorting: {
      enabled: false,
    },

    toolbar: {
      visible: false,
    },

    // Single short, fixed list — no pagination footer.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },

    // Modified-row highlight, re-expressed from the original wrapper class.
    formatting: {
      rowRules: [
        {
          condition: { field: 'IS_MODIFIED', operator: 'equals', value: true },
          className: 'rounded-md border border-primary-200 bg-primary-50/40',
        },
      ],
    },
  };
}

// ---------------------------------------------------------------------------
// EntityForm config (recipe A)
//
// Intentionally thin: no scalar input fields. The `submit` action lives as a
// field descriptor in `fields` (excluded from `<EntityForm.Fields/>`) so the
// field-override bucket (`renameField` / `hideField` keyed `submit`) has a
// stable merge target — `applyLayoutOverrides` only merges `overrides.fields`
// into `config.fields`. The card reads the merged `submit` descriptor to drive
// its own button; EntityForm renders no built-in action buttons (all hidden)
// and never POSTs — the card owns `submitPieceRequests` and the success/lock
// state machine.
// ---------------------------------------------------------------------------

const SUBMIT_FIELD_KEY = 'submit';

const baseFormConfig: EntityFormConfig = {
  id: 'claimflow.requestPiecesCard.form',
  endpoint: {
    // No-op endpoint — the card owns submission; EntityForm never POSTs.
    erpEndpoint: 'claimflow-request-pieces',
    method: 'POST',
  },
  fields: {
    // Override target only — excluded from auto-render (see Fields exclude).
    [SUBMIT_FIELD_KEY]: {
      type: 'text',
      label: 'Enregistrer',
      visible: true,
      order: 1,
    },
  },
  actions: {
    // Card renders its own button; suppress all EntityForm built-in buttons.
    // The button's label + visibility are sourced from the `submit` *field*
    // descriptor above (the card's established rule target), not from this
    // action — so the action stays hidden.
    submit: { visible: false },
    cancel: { visible: false },
    reset: { visible: false },
  },
};

export function RequestPiecesCard({
  idSin,
  _layoutOverrides,
}: Readonly<RequestPiecesCardProps>) {
  const [data, setData] = useState<VeosPieceToRequest[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newState, setNewState] = useState<Record<string, boolean>>({});
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [failedSteps, setFailedSteps] = useState<WorkflowStepExecutionSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pieces = await taskService.fetchPiecesToRequest(Number(idSin));
        if (!cancelled) {
          setData(pieces);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erreur de chargement.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idSin]);

  const initialState = useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    (data ?? []).forEach((p) => {
      map[p.CD_PIE] = p.EN_ATTENTE === 1;
    });
    return map;
  }, [data]);

  const effectiveState = useCallback(
    (cdPie: string): boolean =>
      Object.prototype.hasOwnProperty.call(newState, cdPie)
        ? newState[cdPie]!
        : (initialState[cdPie] ?? false),
    [newState, initialState],
  );

  const modificationCount = useMemo(() => {
    if (!data) return 0;
    return data.filter((p) => effectiveState(p.CD_PIE) !== (p.EN_ATTENTE === 1)).length;
  }, [data, effectiveState]);

  const handleToggle = useCallback((cdPie: string, nextChecked: boolean) => {
    setNewState((prev) => ({ ...prev, [cdPie]: nextChecked }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!data) return;
    const toDemand = data
      .filter((p) => p.EN_ATTENTE === 0 && effectiveState(p.CD_PIE) === true)
      .map((p) => ({ cd_pie: p.CD_PIE }));
    const toCancel = data
      .filter(
        (p) => p.EN_ATTENTE === 1 && effectiveState(p.CD_PIE) === false && p.ID_PMD != null,
      )
      .map((p) => ({ id_pmd: p.ID_PMD as number }));

    if (toDemand.length === 0 && toCancel.length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setFailedSteps([]);
    try {
      await taskService.submitPieceRequests(Number(idSin), toDemand, toCancel);
      setSubmittedCount(modificationCount);
      setSubmitted(true);
      // Re-fold on success (the card may have been expanded by the user); the
      // `key` swap below remounts the Card so it re-reads `defaultExpanded`.
      setExpanded(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Échec de l'enregistrement.");
      setFailedSteps(extractStepExecutions(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [data, effectiveState, idSin, modificationCount]);

  // Build the per-render rows (data + live toggle context) and the merged
  // configs. Rows are rebuilt whenever the data or local state changes so the
  // singleton renderers' closures stay fresh; `applyLayoutOverrides` returns
  // each config unchanged at baseline (no rules) so the render is identical.
  const rows = useMemo<PieceRow[]>(
    () => buildPieceRows(data ?? [], effectiveState, submitted, handleToggle),
    [data, effectiveState, submitted, handleToggle],
  );

  const baseListConfig = useMemo(
    () => createRequestPiecesConfig(rows),
    [rows],
  );

  usePublishLayoutConfig('claimflow.requestPiecesCard', {
    ...buildPublishedConfigFromEntityForm(baseFormConfig, 'Pièces MQ demandées'),
    columns: buildPublishedConfigFromDataList(baseListConfig).columns,
  });

  const listConfig = useMemo(
    () => applyLayoutOverrides(baseListConfig, _layoutOverrides),
    [baseListConfig, _layoutOverrides],
  );

  const formConfig = useMemo(
    () => applyLayoutOverrides(baseFormConfig, _layoutOverrides),
    [_layoutOverrides],
  );

  const list = useDataList(listConfig);
  const form = useEntityForm({ config: formConfig });

  // Merged submit descriptor — label/visibility are override targets. The
  // bespoke button reads them from the `submit` *field* descriptor (the card's
  // established rule target), which the published config surfaces as a field
  // target for the layout editor.
  const submitField = formConfig.fields[SUBMIT_FIELD_KEY];
  const submitVisible = submitField?.visible !== false;
  const submitLabelBase = submitField?.label ?? 'Enregistrer';

  if (isLoading) {
    return (
      <Card id={actionAnchorId('claimflow.requestPiecesCard')}>
        <Card.Header collapsible defaultExpanded={false} action={<Spinner size="sm" />}>
          <Icon icon={FileQuestion} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Pièces MQ demandées', _layoutOverrides)}
          </Text>
        </Card.Header>
      </Card>
    );
  }

  if (error) {
    return (
      <Card id={actionAnchorId('claimflow.requestPiecesCard')}>
        <Card.Header collapsible defaultExpanded={true}>
          <Icon icon={FileQuestion} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Pièces MQ demandées', _layoutOverrides)}
          </Text>
        </Card.Header>
        <Card.Content>
          <Alert variant="error">{error}</Alert>
        </Card.Content>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Card key={submitted ? 'submitted' : 'form'} id={actionAnchorId('claimflow.requestPiecesCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        {submitted ? (
          <Icon icon={CheckCircle2} size="md" color="success" />
        ) : (
          <Icon icon={FileQuestion} size="md" color="primary" />
        )}
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Pièces MQ demandées', _layoutOverrides)}
        </Text>
        <Tooltip content={TOOLTIP_TEXT} position="top">
          <span
            className="inline-flex items-center text-secondary-500 cursor-help"
            aria-label="Aide"
          >
            <Icon icon={HelpCircle} size="sm" color="inherit" />
          </span>
        </Tooltip>
        {submitted && (
          <Text as="span" size="sm" color="muted" className="ml-2">
            — {submittedCount} modification(s)
          </Text>
        )}
      </Card.Header>
      <Card.Content>
        {submitError && (
          <div className="mb-4">
            <Alert variant="error">{submitError}</Alert>
          </div>
        )}
        {failedSteps.length > 0 && (
          <div className="mb-4">
            <Text size="sm" weight="semibold" className="mb-2">
              Détail de l&apos;exécution :
            </Text>
            <WorkflowStepTracker steps={failedSteps} />
          </div>
        )}
        <DataList config={listConfig} list={list}>
          <DataList.Table />
        </DataList>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Fields exclude={[SUBMIT_FIELD_KEY]} />
          <HStack justify="between" align="center" className="pt-4">
            <Text size="sm" color="muted">
              {modificationCount} modification(s)
            </Text>
            {submitVisible && (
              <Button
                type="button"
                variant="primary"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={modificationCount === 0 || isSubmitting || submitted}
              >
                {submitted ? 'Enregistré' : submitLabelBase}
              </Button>
            )}
          </HStack>
        </EntityForm>
      </Card.Content>
    </Card>
  );
}

function extractStepExecutions(error: unknown): WorkflowStepExecutionSummary[] {
  const data = getErrorData(error);
  if (!data) return [];

  const executions = (data as Record<string, unknown>).step_executions;
  if (!Array.isArray(executions)) return [];

  return executions.map((exec: Record<string, unknown>) => ({
    name:
      ((exec.step as Record<string, unknown>)?.name as string) ??
      `Étape ${String(exec.workflow_step_id).slice(0, 8)}`,
    status: exec.status as WorkflowStepExecutionSummary['status'],
    errorMessage: (exec.error_message as string | null) ?? null,
  }));
}

function getErrorData(error: unknown): unknown {
  if (error === null || typeof error !== 'object') return null;
  const err = error as Record<string, unknown>;

  const response = err.response;
  if (response !== null && typeof response === 'object') {
    const respObj = response as Record<string, unknown>;
    const respData = respObj.data;
    if (respData !== null && typeof respData === 'object') {
      const nested = (respData as Record<string, unknown>).data;
      if (nested !== null && typeof nested === 'object') return nested;
      return respData;
    }
  }

  const data = err.data;
  if (data !== null && typeof data === 'object') return data;

  return null;
}
